import React from 'react'
import { StackScreenProps } from '@react-navigation/stack'
import { Checkbox } from '@material-ui/core'
import { ActivityIndicator, View, Text } from 'react-native'
import { DatePicker, Input, Select, SelectOption, SelectSearch, assets } from '@pulseops/common'
import { Controller, useForm } from 'react-hook-form'
import { ZIO } from '@mxt/zio'
import { pipe } from 'fp-ts/function'
import PolicyDispatchingDialog from './components/PolicyDispatchingDialog'
import { PolicyDispatchingService, RdoItem } from './services/PolicyDispatchingService'
import { PolicyDispatchingStackParamList } from './PolicyDispatchingStackParamList'
import {
  borderedDatePickerContainerStyle,
  borderedInputStyle,
  borderedSelectSearchStyle,
  borderedSelectStyle
} from './styles/policyDispatchingStyles'
import { formatDate, parseDateString } from './utils/policyDispatchingUtils'

type FormValues = {
  fullName: string
  gaBp: string
  joinDate: Date | null
  group: string
  office: SelectOption | null
  area: string
  type: { label: string; value: string } | null
  department: string
  inactive: boolean
}

const typeOptions = [
  { value: 'CS', label: 'CS' },
  { value: 'RD', label: 'RD' },
  { value: 'BM', label: 'BM' },
  { value: 'GA', label: 'GA' },
  { value: 'Others', label: 'Others' }
]

type Props = StackScreenProps<PolicyDispatchingStackParamList, 'PDCreateRdoScreen'>

const buildDefaultValues = (rdoItem?: RdoItem | null): FormValues => ({
  fullName: rdoItem?.fullName || '',
  gaBp: rdoItem?.gaBp || '',
  joinDate: parseDateString(rdoItem?.joinDate),
  group: rdoItem?.group || '',
  office: rdoItem?.office ? { value: rdoItem.office, label: rdoItem.office } : null,
  area: rdoItem?.area || '',
  type: rdoItem?.type ? typeOptions.find((option) => option.value === rdoItem.type) || typeOptions[0] : typeOptions[0],
  department: rdoItem?.department || '',
  inactive: Boolean(rdoItem?.inactive)
})

export default function PDCreateRdoScreen({ navigation, route }: Props) {
  const rdoItem = route.params?.rdoItem
  const isEditMode = Boolean(rdoItem)

  const { control, handleSubmit, reset } = useForm<FormValues>({
    defaultValues: buildDefaultValues(rdoItem)
  })
  const [officesOptions, setOfficesOptions] = React.useState<SelectOption[]>([])
  const [newestRdoCode, setNewestRdoCode] = React.useState(rdoItem?.rdoCode || '')
  const [isLoading, setIsLoading] = React.useState(false)
  const [modalInfo, setModalInfo] = React.useState({ title: '', message: '' })
  const [openModal, setOpenModal] = React.useState(false)

  const fetchNewestRdoCode = () => {
    pipe(
      PolicyDispatchingService.getNewestRdoCode,
      ZIO.map((res) => {
        setNewestRdoCode(res.body.newestRdoCode)
      }),
      ZIO.catchAll((error) =>
        ZIO.effectTotal(() => {
          console.error('Unable to load newest RDO code', error)
        })
      ),
      ZIO.unsafeRun({})
    )
  }

  React.useEffect(() => {
    setIsLoading(true)
    pipe(
      PolicyDispatchingService.getOffices,
      ZIO.map((res) => {
        setOfficesOptions(res.body.items.map((office) => ({ value: office.officeCode, label: office.officeCode })))
        setIsLoading(false)
      }),
      ZIO.catchAll((error) =>
        ZIO.effectTotal(() => {
          console.error('Unable to load offices', error)
          setIsLoading(false)
        })
      ),
      ZIO.unsafeRun({})
    )

    if (!isEditMode) {
      fetchNewestRdoCode()
    }
  }, [])

  const handleBack = () => {
    navigation.navigate('PDRDOManagementScreen', { isRequireSearch: true })
  }

  const handleSave = handleSubmit((values) => {
    if (isEditMode) {
      setIsLoading(true)
      pipe(
        PolicyDispatchingService.updateRdo({
          rdoCode: rdoItem?.rdoCode || '',
          fullName: values.fullName,
          joinDate: formatDate(values.joinDate) || '',
          office: values.office?.value || '',
          gaBp: values.gaBp || '',
          area: values.area,
          group: values.group || '',
          department: values.department || '',
          type: values.type?.value || '',
          inactive: values.inactive
        }),
        ZIO.tap((response) =>
          ZIO.effectTotal(() => {
            setIsLoading(false)
            if (response.responseStatus.code !== 1 && response.responseStatus.code !== 200) {
              setModalInfo({ title: 'Chỉnh sửa RDO', message: 'Cập nhật RDO không thành công.' })
              setOpenModal(true)
              return
            }

            setModalInfo({ title: 'Chỉnh sửa RDO', message: 'Cập nhật RDO thành công.' })
            setOpenModal(true)
          })
        ),
        ZIO.catchAll(() =>
          ZIO.effectTotal(() => {
            setIsLoading(false)
            setModalInfo({ title: 'Chỉnh sửa RDO', message: 'Không thể cập nhật RDO. Vui lòng thử lại sau.' })
            setOpenModal(true)
          })
        ),
        ZIO.unsafeRun({})
      )
      return
    }

    if (!newestRdoCode) {
      setModalInfo({ title: 'Tạo RDO mới', message: 'Không thể lấy mã RDO. Vui lòng thử lại sau.' })
      setOpenModal(true)
      return
    }

    setIsLoading(true)
    pipe(
      PolicyDispatchingService.createRdoApprover({
        rdoCode: newestRdoCode,
        fullName: values.fullName,
        joinDate: formatDate(values.joinDate) || '',
        office: values.office?.value || '',
        gaBp: values.gaBp || '',
        area: values.area,
        group: values.group || '',
        department: values.department || '',
        type: values.type?.value || '',
        inactive: values.inactive
      }),
      ZIO.tap((response) =>
        ZIO.effectTotal(() => {
          setIsLoading(false)
          if (response.responseStatus.code !== 1 && response.responseStatus.code !== 200) {
            setModalInfo({
              title: 'Tạo RDO mới',
              message: 'Tạo RDO không thành công. Vui lòng thử lại sau.'
            })
            setOpenModal(true)
            return
          }

          reset()
          fetchNewestRdoCode()
          setModalInfo({ title: 'Tạo RDO mới', message: 'Tạo RDO thành công.' })
          setOpenModal(true)
        })
      ),
      ZIO.catchAll(() =>
        ZIO.effectTotal(() => {
          setIsLoading(false)
          setModalInfo({ title: 'Tạo RDO mới', message: 'Không thể tạo RDO. Vui lòng thử lại sau.' })
          setOpenModal(true)
        })
      ),
      ZIO.unsafeRun({})
    )
  })

  return (
    <View style={{ ...styles.container, position: 'relative' } as any}>
      <PolicyDispatchingDialog
        visible={openModal}
        title={modalInfo.title}
        message={modalInfo.message}
        onClose={() => setOpenModal(false)}
      />
      {isLoading && (
        <View style={styles.loadingOverlay}>
          <ActivityIndicator size="large" color="#ED1B2E" />
        </View>
      )}

      <View style={styles.headerRow}>
        <button type="button" style={styles.backLink} onClick={handleBack}>
          <span style={styles.backArrow}>‹</span>
          Quay lại danh sách
        </button>
        <View style={styles.title}>{isEditMode ? `Chỉnh sửa RDO — ${rdoItem?.rdoCode || ''}` : 'Tạo RDO mới'}</View>
      </View>

      <View style={styles.card}>
        <View style={styles.formGrid}>
          <View style={styles.fieldColumn}>
            <Input
              title={'Mã RDO'}
              value={newestRdoCode}
              onChange={() => {}}
              inputStyle={borderedInputStyle}
              filled
              disabled
              alwayShowUnderline
            />
            <Text style={styles.hintText}>Tự động tạo, không thể chỉnh sửa</Text>
          </View>
          <View style={styles.fieldColumn}>
            <Controller
              name="fullName"
              control={control}
              rules={{ required: { value: true, message: 'Họ và tên là bắt buộc' } }}
              render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
                <Input
                  title={'Họ và tên'}
                  onChange={onChange}
                  onBlur={onBlur}
                  value={value}
                  placeholder={'Nhập họ và tên'}
                  errorMessage={error?.message}
                  inputStyle={borderedInputStyle}
                  filled
                  required
                />
              )}
            />
          </View>

          <View style={styles.fieldColumn}>
            <Controller
              name="joinDate"
              control={control}
              render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
                <DatePicker
                  label={'Ngày gia nhập'}
                  value={value}
                  onChange={onChange}
                  onBlur={onBlur}
                  maxDate={undefined}
                  minDate={undefined}
                  minDateMessage={''}
                  errorMessage={error?.message}
                  inputStyle={{ paddingBottom: 3 }}
                  hideUnderline
                  alwaysShow
                  containerStyle={borderedDatePickerContainerStyle}
                />
              )}
            />
          </View>
          <View style={styles.fieldColumn}>
            <Controller
              name="gaBp"
              control={control}
              render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
                <Input
                  title={'GA/BP'}
                  onChange={onChange}
                  onBlur={onBlur}
                  value={value}
                  placeholder={'Nhập GA/BP'}
                  errorMessage={error?.message}
                  inputStyle={borderedInputStyle}
                  filled
                />
              )}
            />
          </View>

          <View style={styles.fieldColumn}>
            <Controller
              name="office"
              control={control}
              rules={{ required: { value: true, message: 'Văn phòng là bắt buộc' } }}
              render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
                <SelectSearch
                  label={'Văn phòng'}
                  options={officesOptions}
                  placeholder={'Chọn văn phòng'}
                  onChange={onChange}
                  onBlur={onBlur}
                  value={value}
                  key={value?.value}
                  errorMessage={error?.message}
                  inputStyle={borderedSelectSearchStyle}
                  disableUnderline
                  required
                />
              )}
            />
          </View>
          <View style={styles.fieldColumn}>
            <Controller
              name="group"
              control={control}
              render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
                <Input
                  title={'Nhóm'}
                  onChange={onChange}
                  onBlur={onBlur}
                  value={value}
                  placeholder={'Nhập nhóm'}
                  errorMessage={error?.message}
                  inputStyle={borderedInputStyle}
                  filled
                />
              )}
            />
          </View>

          <View style={styles.fieldColumn}>
            <Controller
              name="area"
              control={control}
              rules={{ required: { value: true, message: 'Khu vực là bắt buộc' } }}
              render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
                <Input
                  title={'Khu vực'}
                  onChange={onChange}
                  onBlur={onBlur}
                  value={value}
                  placeholder={'Nhập khu vực'}
                  errorMessage={error?.message}
                  inputStyle={borderedInputStyle}
                  filled
                  required
                />
              )}
            />
          </View>
          <View style={styles.fieldColumn}>
            <Controller
              name="type"
              control={control}
              rules={{ required: { value: true, message: 'Loại là bắt buộc' } }}
              render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
                <Select
                  label={'Loại'}
                  options={typeOptions}
                  placeholder={'Chọn loại'}
                  onChange={(e) => {
                    onChange(e)
                  }}
                  onBlur={onBlur}
                  value={value}
                  key={value?.value}
                  errorMessage={error?.message}
                  containerStyles={borderedSelectStyle}
                  filled
                  required
                />
              )}
            />
          </View>

          <View style={styles.fieldColumn}>
            <Controller
              name="department"
              control={control}
              render={({ field: { onChange, onBlur, value }, fieldState: { error } }) => (
                <Input
                  title={'Ban'}
                  onChange={onChange}
                  onBlur={onBlur}
                  value={value}
                  placeholder={'Nhập ban'}
                  errorMessage={error?.message}
                  inputStyle={borderedInputStyle}
                  filled
                />
              )}
            />
          </View>
          <View style={{ ...styles.fieldColumn, justifyContent: 'center' } as any}>
            <Controller
              name="inactive"
              control={control}
              render={({ field: { onChange, value } }) => (
                <label style={styles.checkboxRow}>
                  <Checkbox
                    checked={Boolean(value)}
                    style={{ color: '#ED1B2E', padding: 0 }}
                    onChange={(event) => onChange(event.target.checked)}
                  />
                  <span style={styles.checkboxLabel}>Ngưng sử dụng</span>
                </label>
              )}
            />
          </View>
        </View>

        <View style={styles.divider} />

        <View style={styles.actionsRow}>
          <button type="button" style={styles.resetButton} onClick={() => reset()} disabled={isLoading}>
            <span style={styles.mr_5}>↺</span>
            Nhập lại
          </button>
          <button type="button" style={styles.saveButton} onClick={handleSave} disabled={isLoading}>
            <span style={styles.mr_5}>
              <assets.PDSave style={{ color: '#fff' }} />
            </span>
            Lưu
          </button>
        </View>
      </View>
    </View>
  )
}

const styles: Record<string, any> = {
  container: {
    paddingTop: 0,
    paddingRight: 40,
    paddingBottom: 30,
    paddingLeft: 40,
    height: '100%',
    overflow: 'auto'
  },
  loadingOverlay: {
    position: 'fixed',
    top: 0,
    right: 0,
    bottom: 0,
    left: 0,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: 'rgba(255, 255, 255, 0.7)',
    zIndex: 2
  },
  headerRow: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-end',
    gap: '16px',
    marginBottom: '18px'
  },
  backLink: {
    background: 'none',
    borderWidth: 0,
    padding: 0,
    color: '#374151',
    fontSize: '14px',
    fontWeight: 500,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: '4px'
  },
  backArrow: {
    fontSize: '18px',
    lineHeight: 1
  },
  title: {
    fontSize: '24px',
    fontWeight: '600'
  },
  card: {
    backgroundColor: '#fff',
    borderRadius: '16px',
    padding: '24px',
    boxShadow: '0 2px 4px rgba(0, 0, 0, 0.08)'
  },
  formGrid: {
    display: 'flex',
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: '20px 24px'
  },
  fieldColumn: {
    width: 'calc(50% - 12px)'
  },
  hintText: {
    fontSize: '12px',
    color: '#94a3b8',
    marginTop: 4
  },
  checkboxRow: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    gap: '8px',
    marginTop: 22,
    cursor: 'pointer'
  },
  checkboxLabel: {
    fontSize: '14px',
    color: '#ED1B2E',
    fontWeight: 500
  },
  divider: {
    borderTopWidth: 1,
    borderTopStyle: 'solid',
    borderTopColor: '#e5e7eb',
    marginTop: 28,
    marginBottom: 20
  },
  actionsRow: {
    display: 'flex',
    flexDirection: 'row',
    gap: '12px'
  },
  resetButton: {
    backgroundColor: '#fff',
    color: '#111827',
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: '#d1d5db',
    borderRadius: '12px',
    paddingTop: 9,
    paddingRight: 16,
    paddingBottom: 9,
    paddingLeft: 16,
    fontSize: '14px',
    fontWeight: 600,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center'
  },
  saveButton: {
    backgroundColor: '#ED1B2E',
    color: '#fff',
    borderWidth: 0,
    borderRadius: '12px',
    paddingTop: 9,
    paddingRight: 16,
    paddingBottom: 9,
    paddingLeft: 16,
    fontSize: '14px',
    fontWeight: 600,
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center'
  },
  mr_5: {
    marginRight: '5px'
  }
}
